// The single <canvas> of the minimap. Redraws only when an input changed (rAF-coalesced) —
// never per frame while idle. The biome layer is cached on an offscreen canvas keyed by the
// terrain signature + viewport + size + dpr; only the dynamic layers repaint otherwise.

import { type ComponentProps, type RefObject, useEffect, useRef, useState } from 'react';
import { clipPolygonToBounds } from './cameraViewBox.js';
import { type PreparedBoard, tileAt } from './minimapBoard.js';
import type { Point } from './minimapGeometry.js';
import { drawDynamic, drawStatic, tileCenterX, tileCenterY } from './minimapPaint.js';
import type { FocusState, HoverState } from './minimapPointer.js';
import type { MinimapLayers } from './minimapPrefs.js';
import type { CanvasSize, Viewport } from './minimapViewport.js';
import type { Ctx2D, PaintView } from './paintTypes.js';
import type { MinimapDetails, MinimapMarker, MinimapPing } from './types.js';

export interface MinimapPerf {
  drawCount: number;
  staticBuilds: number;
  lastDrawMs: number;
}

/** Dev / test handle: window.__aodMinimap (read by scripts/audit and Playwright checks). */
export interface MinimapDebugHandle {
  readonly perf: MinimapPerf;
  tiles(): {
    q: number;
    r: number;
    visibility: string;
    hasCity: boolean;
    hasUnit: boolean;
    owner: number | null;
  }[];
  /** Page (client) coordinates of a tile centre, or null before the first layout. */
  tileCenter(q: number, r: number): { x: number; y: number } | null;
  viewport(): Viewport | null;
  /**
   * The camera view box as it is drawn: the camera's ground footprint clipped to
   * the board. Every point is inside the board's unit bounds.
   */
  cameraPolygon(): Point[] | null;
}

declare global {
  interface Window {
    __aodMinimap?: MinimapDebugHandle;
  }
}

export interface MinimapCanvasProps
  extends Omit<ComponentProps<'canvas'>, 'ref' | 'width' | 'height'> {
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  readonly board: PreparedBoard;
  readonly viewport: Viewport | null;
  readonly size: CanvasSize;
  readonly playerColors?: ReadonlyMap<number, string>;
  readonly markers?: readonly MinimapMarker[];
  readonly details?: MinimapDetails;
  readonly cameraPolygon?: readonly Point[] | null;
  readonly pings?: readonly MinimapPing[];
  readonly layers?: MinimapLayers;
  /** False = static rings (reduced motion / particle effects off). */
  readonly animatePings?: boolean;
  readonly hover: HoverState | null;
  readonly focus: FocusState | null;
  readonly focused: boolean;
  readonly reducedMotion: boolean;
}

export function MinimapCanvas({
  canvasRef,
  board,
  viewport,
  size,
  playerColors,
  markers,
  details,
  cameraPolygon,
  pings,
  layers,
  animatePings,
  hover,
  focus,
  focused,
  reducedMotion,
  ...rest
}: MinimapCanvasProps) {
  const staticRef = useRef<{ canvas: HTMLCanvasElement; key: string } | null>(null);
  const perf = useRef<MinimapPerf>({ drawCount: 0, staticBuilds: 0, lastDrawMs: 0 });
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const boardRef = useRef(board);
  boardRef.current = board;
  const cameraPolygonRef = useRef(cameraPolygon);
  cameraPolygonRef.current = cameraPolygon;

  // Pulsing markers and active pings animate at ~8 fps, and only while something
  // exists to animate (and motion is allowed).
  const animating =
    !reducedMotion &&
    animatePings !== false &&
    ((markers?.some((m) => m.pulsing) ?? false) || (pings?.length ?? 0) > 0);
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (!animating) return;
    const id = setInterval(() => setPhase((p) => p + 0.7), 125);
    return () => clearInterval(id);
  }, [animating]);

  const hoverQ = hover?.q;
  const hoverR = hover?.r;
  const focusQ = focused ? focus?.q : undefined;
  const focusR = focused ? focus?.r : undefined;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !viewport || size.width <= 0 || size.height <= 0) return;
    const dpr = typeof window === 'undefined' ? 1 : Math.max(1, window.devicePixelRatio || 1);
    const bw = Math.round(size.width * dpr);
    const bh = Math.round(size.height * dpr);
    const frame = requestAnimationFrame(() => {
      const t0 = performance.now();
      if (canvas.width !== bw) canvas.width = bw;
      if (canvas.height !== bh) canvas.height = bh;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const view: PaintView = { ...viewport, width: size.width, height: size.height };

      // Static layer (biomes): rebuilt only when the terrain / viewport / size / dpr changed.
      // Rivers + relief are painted into the static layer, so their toggles
      // must bust the cache (territory/fog/units are dynamic and need not).
      const staticLayers = `r${layers?.rivers === false ? 0 : 1}f${layers?.relief === false ? 0 : 1}`;
      const key = `${board.terrainSignature}|${staticLayers}|${viewport.scale.toFixed(3)}|${viewport.panX.toFixed(2)}|${viewport.panY.toFixed(2)}|${bw}x${bh}`;
      let cache = staticRef.current;
      if (!cache) {
        cache = { canvas: document.createElement('canvas'), key: '' };
        staticRef.current = cache;
      }
      if (cache.key !== key) {
        cache.canvas.width = bw;
        cache.canvas.height = bh;
        const sctx = cache.canvas.getContext('2d');
        if (sctx) {
          sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          drawStatic(sctx as unknown as Ctx2D, board, view, layers);
          cache.key = key;
          perf.current.staticBuilds++;
        }
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bw, bh);
      ctx.drawImage(cache.canvas, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawDynamic(ctx as unknown as Ctx2D, board, view, {
        playerColors,
        markers,
        details,
        cameraPolygon,
        pings,
        layers,
        nowMs: performance.now(),
        animatePings: animatePings !== false,
        hover: hoverQ === undefined || hoverR === undefined ? null : { q: hoverQ, r: hoverR },
        focus: focusQ === undefined || focusR === undefined ? null : { q: focusQ, r: focusR },
        phase,
        reducedMotion,
      });

      perf.current.drawCount++;
      perf.current.lastDrawMs = performance.now() - t0;
    });
    return () => cancelAnimationFrame(frame);
  }, [
    canvasRef,
    board,
    viewport,
    size,
    playerColors,
    markers,
    details,
    cameraPolygon,
    pings,
    layers,
    animatePings,
    phase,
    hoverQ,
    hoverR,
    focusQ,
    focusR,
    reducedMotion,
  ]);

  // Debug / test handle.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle: MinimapDebugHandle = {
      perf: perf.current,
      tiles: () =>
        boardRef.current.tiles.map((t) => ({
          q: t.q,
          r: t.r,
          visibility: t.visibility,
          hasCity: t.hasCity,
          hasUnit: t.hasUnit,
          owner: t.controlledBy,
        })),
      tileCenter: (q, r) => {
        const el = canvasRef.current;
        const vp = viewportRef.current;
        if (!el || !vp || !tileAt(boardRef.current, q, r)) return null;
        const rect = el.getBoundingClientRect();
        const view = { ...vp, width: rect.width, height: rect.height };
        return { x: rect.left + tileCenterX(view, q, r), y: rect.top + tileCenterY(view, r) };
      },
      viewport: () => viewportRef.current,
      cameraPolygon: () => {
        const poly = cameraPolygonRef.current;
        if (!poly || poly.length < 3) return null;
        const clipped = clipPolygonToBounds(poly, boardRef.current.bounds);
        // What the painter actually draws (a clipped polygon under three points
        // is not drawable).
        return clipped.length >= 3 ? clipped : null;
      },
    };
    window.__aodMinimap = handle;
    return () => {
      if (window.__aodMinimap === handle) window.__aodMinimap = undefined;
    };
  }, [canvasRef]);

  return (
    <canvas
      ref={canvasRef}
      // The backing store is sized in the draw effect; CSS size fills the container.
      style={{ width: '100%', height: '100%', touchAction: 'none' }}
      {...rest}
    />
  );
}
